import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, KeyRound, ArrowDownToLine, ArrowUpFromLine, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/cn";
import { Badge } from "@/components/Badge";
import { Modal } from "@/components/Modal";
import { QueryError } from "@/components/QueryError";
import { AdminRecordActions } from "@/components/AdminRecordActions";

type KeyType = "UNIT" | "COMMON_AREA" | "MAINTENANCE" | "MASTER";
type KeyStatus = "ACTIVE" | "LOST" | "RETURNED";

interface KeyRow {
  id: string;
  unitId?: string | null;
  keyType: KeyType;
  keyCode: string;
  assignedTo?: string | null;
  assignedAt?: string | null;
  returnedAt?: string | null;
  status: KeyStatus;
  notes?: string | null;
  unit?: { unitNumber: string } | null;
}

const statusVariant: Record<KeyStatus, "success" | "danger" | "default"> = {
  ACTIVE: "success",
  LOST: "danger",
  RETURNED: "default",
};

export function KeysPage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<KeyRow | null>(null);
  const [assignFor, setAssignFor] = useState<KeyRow | null>(null);

  const { data: keys = [], isLoading, isError } = useQuery({
    queryKey: ["keys"],
    queryFn: async () => (await api.get<KeyRow[]>("/keys")).data,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/keys/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["keys"] }),
  });

  const checkInMutation = useMutation({
    mutationFn: async (id: string) => (await api.post(`/keys/${id}/check-in`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["keys"] }),
  });
  const lostMutation = useMutation({
    mutationFn: async (id: string) => (await api.post(`/keys/${id}/lost`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["keys"] }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <KeyRound size={24} /> {t("security.keys")}
        </h1>
        <button onClick={() => setOpen(true)} className="btn-primary">
          <Plus size={16} /> {t("security.newKey")}
        </button>
      </div>

      <div className="card p-0 overflow-x-auto">
        {isError ? (
          <div className="p-6"><QueryError /></div>
        ) : isLoading ? (
          <div className="p-6 text-slate-500">{t("common.loading")}</div>
        ) : keys.length === 0 ? (
          <div className="p-6 text-slate-500">—</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2">{t("security.keyCode")}</th>
                <th className="text-left px-4 py-2">{t("security.type")}</th>
                <th className="text-left px-4 py-2">Unidad</th>
                <th className="text-left px-4 py-2">{t("security.assignedTo")}</th>
                <th className="text-left px-4 py-2">{t("finance.status")}</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {keys.map((k) => (
                <tr key={k.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2 font-medium">{k.keyCode}</td>
                  <td className="px-4 py-2">{t(`security.keyTypes.${k.keyType}`)}</td>
                  <td className="px-4 py-2">{k.unit?.unitNumber ?? "—"}</td>
                  <td className="px-4 py-2">
                    {k.assignedTo ?? "—"}
                    {k.assignedAt && <div className="text-xs text-slate-500">{formatDate(k.assignedAt, i18n.language)}</div>}
                  </td>
                  <td className="px-4 py-2"><Badge variant={statusVariant[k.status]}>{t(`security.keyStatus.${k.status}`)}</Badge></td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex gap-1 justify-end">
                      {!k.assignedTo && k.status !== "LOST" && (
                        <button className="btn-primary text-xs" onClick={() => setAssignFor(k)}>
                          <ArrowDownToLine size={12} /> {t("security.checkOut")}
                        </button>
                      )}
                      {k.assignedTo && (
                        <button className="btn-secondary text-xs" onClick={() => checkInMutation.mutate(k.id)}>
                          <ArrowUpFromLine size={12} /> {t("security.checkIn")}
                        </button>
                      )}
                      {k.status !== "LOST" && (
                        <button
                          className="btn bg-red-50 text-red-700 hover:bg-red-100 text-xs"
                          onClick={() => { if (confirm("¿Marcar como perdida?")) lostMutation.mutate(k.id); }}
                        >
                          <AlertTriangle size={12} /> {t("security.markLost")}
                        </button>
                      )}
                      <AdminRecordActions
                        compact
                        entityLabel={k.keyCode}
                        onEdit={() => setEditing(k)}
                        onDelete={() => deleteMutation.mutate(k.id)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <NewKeyModal
        open={open || !!editing}
        editing={editing}
        onClose={() => { setOpen(false); setEditing(null); }}
        onDone={() => qc.invalidateQueries({ queryKey: ["keys"] })}
      />
      <AssignModal keyRow={assignFor} onClose={() => setAssignFor(null)} onDone={() => qc.invalidateQueries({ queryKey: ["keys"] })} />
    </div>
  );
}

function NewKeyModal({ open, editing, onClose, onDone }: { open: boolean; editing?: KeyRow | null; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const { data: units = [] } = useQuery({
    queryKey: ["units"],
    queryFn: async () => (await api.get("/units")).data,
    enabled: open,
  });
  const [form, setForm] = useState({
    keyCode: "",
    keyType: "UNIT" as KeyType,
    unitId: "",
    notes: "",
  });

  useEffect(() => {
    if (editing) {
      setForm({
        keyCode: editing.keyCode,
        keyType: editing.keyType,
        unitId: editing.unitId ?? "",
        notes: editing.notes ?? "",
      });
    } else {
      setForm({ keyCode: "", keyType: "UNIT", unitId: "", notes: "" });
    }
  }, [editing]);

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        ...form,
        unitId: form.unitId || null,
        notes: form.notes || null,
      };
      return editing
        ? (await api.put(`/keys/${editing.id}`, payload)).data
        : (await api.post("/keys", payload)).data;
    },
    onSuccess: () => {
      onDone();
      onClose();
      setForm({ keyCode: "", keyType: "UNIT", unitId: "", notes: "" });
    },
  });

  return (
    <Modal open={open} onClose={onClose} title={editing ? t("security.keys") : t("security.newKey")}>
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }}>
        <div>
          <label className="label">{t("security.keyCode")}</label>
          <input className="input" required value={form.keyCode} onChange={(e) => setForm({ ...form, keyCode: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">{t("security.type")}</label>
            <select className="input" value={form.keyType} onChange={(e) => setForm({ ...form, keyType: e.target.value as KeyType })}>
              {(["UNIT", "COMMON_AREA", "MAINTENANCE", "MASTER"] as KeyType[]).map((k) => (
                <option key={k} value={k}>{t(`security.keyTypes.${k}`)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Unidad (opcional)</label>
            <select className="input" value={form.unitId} onChange={(e) => setForm({ ...form, unitId: e.target.value })}>
              <option value="">—</option>
              {(units as any[]).map((u) => (
                <option key={u.id} value={u.id}>{u.unitNumber}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className="label">{t("security.notes")}</label>
          <input className="input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>{t("common.cancel")}</button>
          <button type="submit" className="btn-primary" disabled={mutation.isPending}>{t("common.save")}</button>
        </div>
      </form>
    </Modal>
  );
}

function AssignModal({ keyRow, onClose, onDone }: { keyRow: KeyRow | null; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [assignedTo, setAssignedTo] = useState("");
  const mutation = useMutation({
    mutationFn: async () => (await api.post(`/keys/${keyRow!.id}/check-out`, { assignedTo })).data,
    onSuccess: () => {
      onDone();
      onClose();
      setAssignedTo("");
    },
  });
  if (!keyRow) return null;
  return (
    <Modal open={!!keyRow} onClose={onClose} title={`${t("security.checkOut")} — ${keyRow.keyCode}`}>
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }}>
        <div>
          <label className="label">{t("security.assignedTo")}</label>
          <input className="input" required placeholder="Nombre completo" value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>{t("common.cancel")}</button>
          <button type="submit" className="btn-primary" disabled={mutation.isPending}>{t("common.save")}</button>
        </div>
      </form>
    </Modal>
  );
}
